import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, Form, Button, Alert, Spinner, Row, Col } from "react-bootstrap";
import { CheckCircleFill } from "react-bootstrap-icons";

function Evaluation() {
  const [statements, setStatements] = useState([]);
  const [scaleLabels, setScaleLabels] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false); 
  
  const navigate = useNavigate();

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
    
    const fetchEvaluationData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/evaluation_statements`, {
          credentials: "include",
        });
        
        const data = await response.json();

        if (response.ok) {
          if (data.already_completed) {
            setAlreadyCompleted(true);
          } else {
            setStatements(data.statements);
            setScaleLabels(data.scale_labels); 
          }
        } else {
          setError("Failed to load evaluation data.");
        }
      } catch (err) {
        setError("A network error occurred while loading the evaluation.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluationData();
  }, []);

  const handleOptionChange = (statementId, value) => {
    setResponses((prev) => ({
      ...prev,
      [statementId]: value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Ensure every statement has an answer
    if (Object.keys(responses).length < statements.length) {
      setError("Please provide a response for all statements before submitting.");
      return;
    }

    setSubmitting(true);
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

    // Format the payload to match Rails strong params
    const payload = {
      evaluation: {
        evaluation_responses_attributes: Object.entries(responses).map(([statementId, score]) => ({
          evaluation_statement_id: parseInt(statementId, 10),
          score: parseInt(score, 10),
        })),
      },
    };

    try {
      const response = await fetch(`${API_URL}/api/v1/evaluations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.errors ? data.errors.join(", ") : "Submission failed.");
      }
    } catch (err) {
      setError("A network error occurred during submission.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (alreadyCompleted) {
    return (
      <Container className="evaluation">
        <div className="evaluation-header pt-5">
          <CheckCircleFill color="green" size={64} className="mb-5" />
          <h2 className="mb-3">Evaluation Complete</h2>
          <p className="evaluation-prompt pb-5">You have already submitted your course evaluation. Thank you!</p>
          <Button className="app-button mb-5" size="lg" onClick={() => navigate("/")}>
            Return to Home
          </Button>
        </div>
      </Container>
    );
  }

  if (success) {
    return (
      <Container className="evaluation">
        <div className="evaluation-header pt-5">
          <CheckCircleFill color="green" size={64} className="mb-5" />
          <h2 className="mb-3">Thank You!</h2>
          <p className="evaluation-prompt pb-5">Your course evaluation has been submitted successfully.</p>
          <Button className="app-button mb-5" size="lg" onClick={() => navigate("/")}>
            Return to Home
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="evaluation py-5">
      <Card className="shadow-sm border-0">
        <Card.Header className="evaluation-header">
          <div className="evaluation-title pt-5 pb-4">Course Evaluation</div>
          <p className="evaluation-prompt pb-4 text-center">
            Please complete this brief evaluation to provide us with feedback about your experience using CTGWeb2.0. After submitting the evaluation, you will receive your completion certificate. Thank you!
          </p>
        </Card.Header>
        <Card.Body className="p-4 p-md-5">

          {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            {statements.map((statement, index) => (
              <div key={statement.id} className="mb-4 pb-4 border-bottom">
                <Form.Label className="fw-bold fs-5 mb-3">
                  {index + 1}. {statement.content}
                </Form.Label>
                <Row className="gy-2">
                  {scaleLabels.map((label) => (
                    <Col key={label.value} xs={12} sm="auto" className="me-sm-4">
                      <Form.Check
                        type="radio"
                        id={`statement-${statement.id}-option-${label.value}`}
                        name={`statement-${statement.id}`}
                        label={label.label}
                        value={label.value}
                        onChange={() => handleOptionChange(statement.id, label.value)}
                        checked={responses[statement.id] === label.value}
                        className="custom-radio"
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            ))}

            <div className="d-flex justify-content-end mt-5">
              <Button
                variant="outline-secondary"
                className="me-3"
                onClick={() => navigate("/")}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button className="app-button" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Evaluation"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Evaluation;